/**
 * One scenario type's scenarios: the list on the left, the one being read beside it, and
 * the picks for a bulk run or a dataset made on the same rows rather than a second screen.
 * The type's tools sit behind a reveal because they matter while judging a run, not while
 * choosing one.
 */

import type { ReactNode } from 'react';
import { ArrowLeft, History, MessagesSquare, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContainedScrollArea } from '@/components/ui/contained-scroll-area';
import type { ScenarioCounts } from '@/services/ai-function.service';
import { Disclosure } from './disclosure';
import { LevelHeader } from './level-header';
import { ScenarioDetail } from './scenario-detail';
import { countsLine } from './scenario-counts';
import { PanelState } from '@/modules/simulator/ui/panel-state';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { ScenarioRows, ScenarioRowsEmpty, ScenarioRowsSkeleton } from './scenario-rows';
import type { Scenario, ScenarioType } from '../types';

interface TypeDetailProps {
	/** Null for the untyped group: past tickets that fit no type, which no bulk run takes. */
	type: ScenarioType | null;
	scenarios: Scenario[];
	/** The server's counts for the type, known before its rows arrive. */
	counts: ScenarioCounts;
	hydrated: boolean;
	/** This type's scenarios are on their way; they are fetched when it is opened. */
	loadingScenarios: boolean;
	loadFailed: boolean;
	/** The scenario being read beside the list. */
	activeScenario: Scenario | null;
	selectedIds: Set<string>;
	selectionFull: boolean;
	/** Absent where nothing is picked: the untyped group, or a type other than the dataset's being picked for. */
	onToggleSelected?: (scenario: Scenario) => void;
	inDatasetIds?: ReadonlySet<string>;
	/** Fabricate on the type, offered in the header and when it has nothing under it. */
	fabricate?: ReactNode;
	/** A refusal about the type, under its header. */
	notice?: ReactNode;
	/** Fabricated threads streaming in under the rows; nothing while no stream runs for this type. */
	streaming?: ReactNode;
	/** What the pick is for, under the rows. */
	footer?: ReactNode;
	onBack: () => void;
	onOpenBulkRuns: () => void;
	onDeleteType: () => void;
	onOpenScenario: (scenario: Scenario) => void;
	onTestScenario: (scenario: Scenario) => void;
	onDeleteScenario: (scenario: Scenario) => void;
	onViewTicket: (ticketId: string) => void;
	onHistory?: (scenario: Scenario) => void;
}

export const TypeDetail = ({
	type,
	scenarios,
	counts,
	hydrated,
	loadingScenarios,
	loadFailed,
	activeScenario,
	selectedIds,
	selectionFull,
	onToggleSelected,
	inDatasetIds,
	fabricate,
	notice,
	streaming,
	footer,
	onBack,
	onOpenBulkRuns,
	onDeleteType,
	onOpenScenario,
	onTestScenario,
	onDeleteScenario,
	onViewTicket,
	onHistory
}: TypeDetailProps) => {
	const settled = hydrated && !loadingScenarios;
	const nothingHere = settled && !loadFailed && scenarios.length === 0 && !streaming;

	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<div className='flex shrink-0 flex-col'>
				<LevelHeader
					onBack={onBack}
					backLabel='Back to scenario types'
					title={type ? type.description : 'Past tickets with no scenario type'}
					subtitle={
						type
							? countsLine(counts)
							: 'Past tickets mapped to this protocol that fit none of its scenario types. Each may be one the protocol does not cover yet.'
					}
					actions={
						type && (
							<>
								{fabricate}
								<PillButton className='gap-1' onClick={onOpenBulkRuns}>
									<History className='size-3' />
									Bulk runs
								</PillButton>
								<Button
									variant='ghost'
									size='icon'
									className='size-7 text-gray-900 hover:text-destructive'
									onClick={onDeleteType}
									aria-label='Delete scenario type'
									title='Delete scenario type'
								>
									<Trash2 className='size-3.5' />
								</Button>
							</>
						)
					}
				/>
				{notice}

				{/* What the type is, one disclosure away: it settles a verdict, it does
				    not help choose which scenario to run. */}
				{type && (
					<Disclosure label='View more details' openLabel='View less details' className='border-b border-gray-500'>
						<div className='flex flex-col gap-3 pb-1'>
							<div className='flex flex-col gap-1'>
								<p className='text-xs leading-4 text-gray-800'>What the protocol prescribes</p>
								<p className='text-[13px] leading-4.5 text-text-body'>{type.outcome}</p>
							</div>
							<div className='flex flex-col gap-1'>
								<p className='text-xs leading-4 text-gray-800'>
									{type.tools.length ? `Tools this type calls (${type.tools.length})` : 'Tools this type calls'}
								</p>
								{type.tools.length ? (
									// Numbered: the protocol prescribes a sequence, and a run that refunds
									// before it has read the order is wrong.
									<ol className='flex flex-col gap-1'>
										{type.tools.map((tool, index) => (
											<li key={`${tool.name}-${index}`} className='flex items-baseline gap-1.5'>
												<span className='w-3 shrink-0 text-[10px] tabular-nums text-gray-800'>{index + 1}</span>
												<code className='shrink-0 rounded bg-gray-300 px-1 py-px font-mono text-[11px] text-text-body'>
													{tool.name}
												</code>
												{tool.expect && (
													<span className='min-w-0 flex-1 text-[11px] leading-4 text-gray-800'>→ {tool.expect}</span>
												)}
											</li>
										))}
									</ol>
								) : (
									<p className='text-[13px] leading-4.5 text-gray-800'>None recorded for this type.</p>
								)}
							</div>
						</div>
					</Disclosure>
				)}
			</div>

			{nothingHere ? (
				<PanelState icon={<MessagesSquare className='size-5' />}>
					<span className='font-medium text-text-body'>
						{type ? 'No scenarios under this scenario type yet' : 'Every past ticket fits a scenario type'}
					</span>
					<span className='max-w-88'>
						{type
							? 'Past tickets arrive here when a mapping pass places them under this type. Fabricate customers to test it without waiting for one.'
							: 'Nothing was left over by the last mapping pass, so there is nothing to read here.'}
					</span>
					<div className='mt-1 flex items-center gap-2'>
						{type && fabricate}
						<PillButton onClick={onBack}>
							<ArrowLeft className='size-3' />
							Back to scenario types
						</PillButton>
					</div>
				</PanelState>
			) : (
				<div className='grid min-h-0 flex-1 grid-cols-[minmax(240px,38%)_minmax(0,1fr)]'>
					{/* A grid, not a flex row: a flex item's min-width is auto, so the right pane's own
					    buttons would push the page sideways. minmax(0, 1fr) has no such floor. */}
					<div className='flex min-h-0 min-w-0 flex-col border-r border-gray-500'>
						<ContainedScrollArea>
							{!hydrated || loadingScenarios ? (
								<ScenarioRowsSkeleton />
							) : loadFailed && !scenarios.length ? (
								<ScenarioRowsEmpty>The scenarios could not be loaded. Reload to retry.</ScenarioRowsEmpty>
							) : (
								<>
									<ScenarioRows
										scenarios={scenarios}
										activeId={activeScenario?.id ?? null}
										selectedIds={selectedIds}
										onToggleSelected={onToggleSelected}
										selectionFull={selectionFull}
										inDatasetIds={inDatasetIds}
										onOpen={onOpenScenario}
										onTest={onTestScenario}
										onDelete={onDeleteScenario}
										onHistory={onHistory}
									/>
									{streaming}
								</>
							)}
						</ContainedScrollArea>
					</div>

					<div className='flex min-h-0 min-w-0 flex-col'>
						{activeScenario ? (
							<ScenarioDetail
								key={activeScenario.id}
								scenario={activeScenario}
								onTest={onTestScenario}
								onViewTicket={onViewTicket}
								onHistory={onHistory}
							/>
						) : (
							<PanelState icon={<MessagesSquare className='size-5' />}>
								<span className='max-w-60'>
									{onToggleSelected
										? inDatasetIds
											? 'Pick a scenario to read it here, or tick the ones to add to the dataset.'
											: 'Pick a scenario to read it here, or tick the ones a bulk run takes.'
										: 'Pick a scenario to read it here.'}
								</span>
							</PanelState>
						)}
					</div>
				</div>
			)}

			{!nothingHere && footer}
		</div>
	);
};
